import { ReportHeader } from '../../../pages/reports/header/report-header';
import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { DataTablePageEvent } from 'primereact/datatable';
import { formatDate } from '../../../utils/date-format';
import { AccountsPaymentsSideBarFilter } from './side-bar/accounts-payments-side-bar-filter';
import { getCrmAccountsPaymentsReportOperation } from './operations/get-crm-accounts-payments-report-operation';
import { AccountsPaymentsSideBarFilterType } from './side-bar/accounts-payments-side-bar-filter.type';
import { CrmAccountsPaymentsReportRow } from '../../../api/types/reports/crm/crm-accounts-payments-report.type';
import { Tag } from 'primereact/tag';
import { CrmOperationType } from '../../../api/types/crm/payments/crm-payments-entity';
import { PaymentMethodsDb } from '../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { CrmAccountType } from '../../../api/types/crm/accounts/crm-account-entity';

const paymentMethodHebrew = (method: PaymentMethodsDb | null | undefined) => {
  switch (method) {
    case PaymentMethodsDb.CreditCard:
      return 'כרטיס אשראי';
    case PaymentMethodsDb.BankTransfer:
      return 'העברה בנקאית';
    case PaymentMethodsDb.Check:
      return 'המחאה';
    case PaymentMethodsDb.Scholarship:
      return 'מלגה';
    case PaymentMethodsDb.Cash:
      return 'מזומן';
    case PaymentMethodsDb.Discount:
      return 'הנחה';
    default:
      return 'חסר';
  }
};

const operationTypeHebrew = (value: CrmOperationType | null | undefined) => {
  const normalized = (value ?? '').toLowerCase();
  if (normalized === 'payment') return 'תשלום';
  if (normalized === 'refund') return 'החזר';
  return 'חסר';
};

const crmAccountTypeHebrew = (type: CrmAccountType | null | undefined) => {
  switch (type) {
    case CrmAccountType.Company:
      return 'חברה';
    case CrmAccountType.Association:
      return 'עמותה';
    case CrmAccountType.NonProfit:
      return 'מלכ״ר';
    case CrmAccountType.SelfEmployed:
      return 'עצמאי';
    case CrmAccountType.Government:
      return 'גוף ממשלתי';
    case CrmAccountType.Institution:
      return 'מוסד';
    case CrmAccountType.Individual:
      return 'פרטי';
    case CrmAccountType.Other:
      return 'אחר';
    default:
      return 'חסר';
  }
};

export const AccountsPaymentsPage = () => {
  const [data, setData] = useState<CrmAccountsPaymentsReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState<AccountsPaymentsSideBarFilterType>({
    branchIds: [],
    crmAccountTypes: [],
    paymentMethods: [],
    minAmount: null,
    maxAmount: null,
    fromDate: null,
    toDate: null,
  });

  const fetchReportData = async (
    nextFilters: AccountsPaymentsSideBarFilterType,
    newOffset: number = offset,
    newLimit: number = limit,
  ) => {
    setFilters(nextFilters);
    setOffset(newOffset);
    setLimit(newLimit);
    await getCrmAccountsPaymentsReportOperation(
      nextFilters,
      newOffset,
      newLimit,
      setData,
      setTotal,
      setLoading,
    );
  };

  useEffect(() => {
    fetchReportData(filters);
  }, []);

  const handleOnSearch = async (
    searchFilters: AccountsPaymentsSideBarFilterType,
  ) => {
    await fetchReportData(searchFilters, 0, limit);
  };

  const onPageChange = (event: DataTablePageEvent) => {
    fetchReportData(filters, event.first, event.rows);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח תשלומי חשבונות (CRM)"
        description="דו״ח זה מציג תשלומים שנקלטו במודול ה-CRM לפי שלוחה, סוג חשבון, אמצעי תשלום, סכום וטווח תאריכים."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'מעקב וסינון תשלומי CRM' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="financial"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <AccountsPaymentsSideBarFilter onSearch={handleOnSearch} />
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div
            style={{ fontSize: '14px', color: '#4b5563', textAlign: 'right' }}
          >
            סה״כ רשומות: {total.toLocaleString()}
          </div>

          <DataTable
            value={data}
            rowHover
            loading={loading}
            paginator
            rows={limit}
            first={offset}
            totalRecords={total}
            onPage={onPageChange}
            lazy={true}
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="crmPaymentId"
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{ direction: 'rtl' }}
          >
            <Column
              field="crmPaymentId"
              header="מזהה תשלום"
              style={{ textAlign: 'right', width: '110px' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="crmAccountId"
              header="מזהה חשבון"
              style={{ textAlign: 'right', width: '110px' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="accountName"
              header="שם חשבון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="accountType"
              header="סוג חשבון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmAccountsPaymentsReportRow) =>
                crmAccountTypeHebrew(row.accountType)
              }
            />
            <Column
              field="branchName"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmAccountsPaymentsReportRow) =>
                row.branchName?.trim() ? row.branchName : 'לא קיים'
              }
            />
            <Column
              field="paymentAmount"
              header="סכום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmAccountsPaymentsReportRow) =>
                typeof row.paymentAmount === 'number'
                  ? row.paymentAmount.toFixed(2)
                  : 'חסר'
              }
            />
            <Column
              field="paymentMethodType"
              header="אמצעי תשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmAccountsPaymentsReportRow) =>
                paymentMethodHebrew(row.paymentMethodType)
              }
            />
            <Column
              field="operationType"
              header="סוג פעולה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmAccountsPaymentsReportRow) => {
                const normalized = (row.operationType ?? '').toLowerCase();
                const isRefund = normalized === 'refund';
                const label = operationTypeHebrew(row.operationType);
                const severity = isRefund ? 'warning' : 'success';
                return <Tag value={label} severity={severity as any} />;
              }}
            />
            <Column
              field="createdAt"
              header="תאריך יצירה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmAccountsPaymentsReportRow) =>
                row.createdAt ? formatDate(row.createdAt as any) : 'חסר'
              }
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};
